import React from "react";
import { SlaMeasure, TicketSlaCompliance } from "../../interfaces/Ticket";
import { contagens, fmtPercent, labelMes, slaTone, TONE_BAR, TONE_TEXT } from "./slaFormat";

export const SLA_RULE =
  "Conta os chamados abertos no mês. Resolver ou responder até o prazo conta como no prazo; chamados ainda dentro do prazo ficam fora do percentual.";

const PercentCard: React.FC<{ title: string; measure: SlaMeasure }> = ({ title, measure }) => (
  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-navy-900">
    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{title}</p>
    <p className={`mt-1 font-display text-3xl font-extrabold tabular-nums ${TONE_TEXT[slaTone(measure.percent)]}`}>
      {fmtPercent(measure.percent)}
    </p>
    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-700 dark:text-gray-300">
      {contagens(measure).map((c) => (
        <div key={c.label} className="flex items-center justify-between gap-2">
          <dt>{c.label}</dt>
          <dd className="font-semibold tabular-nums text-navy-700 dark:text-white">{c.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/** Barras simples: altura = % resolvidos no prazo do mês; mês sem base fica como um traço. */
const Trend: React.FC<{ data: TicketSlaCompliance["byMonth"] }> = ({ data }) => (
  <div>
    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Resolvidos no prazo, últimos 6 meses</p>
    <ul className="mt-2 flex h-24 items-end gap-2">
      {data.map((m) => {
        const p = m.resolution.percent;
        const desc = `${labelMes(m.month)}: ${p == null ? "sem base para calcular" : fmtPercent(p)} resolvidos no prazo, ${m.total} chamados`;
        return (
          <li key={m.month} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
            <span className="text-[10px] font-semibold tabular-nums text-gray-700 dark:text-gray-300">
              {fmtPercent(p)}
            </span>
            <div
              role="img"
              aria-label={desc}
              title={desc}
              className={`w-full max-w-[28px] rounded-t-md ${TONE_BAR[slaTone(p)]}`}
              style={{ height: p == null ? "3px" : `${Math.max(p, 4) * 0.55}%` }}
            />
            <span className="text-[10px] text-gray-600 dark:text-gray-400">{labelMes(m.month)}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

/** Parte comum ao painel interno e ao resumo do portal: percentuais, contagens e tendência. */
const SlaSummary: React.FC<{ data: TicketSlaCompliance }> = ({ data }) => (
  <div className="flex flex-col gap-4">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <PercentCard title="Resolvidos no prazo" measure={data.resolution} />
      <PercentCard title="Primeira resposta no prazo" measure={data.response} />
    </div>
    {data.byMonth.length > 0 && <Trend data={data.byMonth} />}
  </div>
);

export default SlaSummary;
